import {modalController} from '@ionic/vue';
import {Preferences} from '@capacitor/preferences';
import {Observable, ObservableEvent} from '@/services/observable/Observable';
import ApiService from '@/services/api/apiService';
import PremiumCheckoutPage from '@/views/common/PremiumCheckoutPage.vue';
import {PreferenceKey} from '@/definitions/PreferenceKey';

type BillingJson = {
    success?: boolean;
    data?: any;
    message?: string;
    error?: string;
};

/**
 * Web PWA billing via this Laravel app's /api/billing routes (partner Ryft card store).
 * Uses absolute /api/billing paths so requests do not go through /pwa-api.
 */
class BillingService {
    private async authHeader(): Promise<Record<string, string>> {
        const token = (await Preferences.get({key: PreferenceKey.ApiToken})).value;
        return token ? {Authorization: `Bearer ${token}`} : {};
    }

    private async request(method: string, path: string, body?: Record<string, unknown>): Promise<any> {
        const headers: Record<string, string> = {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(await this.authHeader()),
        };

        const response = await fetch(`/api/billing/${path.replace(/^\//, '')}`, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
            credentials: 'same-origin',
        });

        let payload: BillingJson | null = null;
        try {
            payload = await response.json();
        } catch {
            payload = null;
        }

        if (!response.ok || payload?.success === false) {
            const message = payload?.message || payload?.error || `Billing request failed (${response.status})`;
            throw new Error(typeof message === 'string' ? message : 'Billing request failed');
        }

        return payload?.data ?? payload;
    }

    async configure(_userId?: string | number | null): Promise<void> {
        // no-op
    }

    async login(_userId?: string | number | null): Promise<void> {
        // no-op
    }

    async logout(): Promise<void> {
        // no-op
    }

    async presentPaywall(
        placement = 'premium',
        options?: { title?: string; text?: string },
    ): Promise<void> {
        const modal = await modalController.create({
            component: PremiumCheckoutPage,
            cssClass: 'auto-height premium-paywall-modal',
            componentProps: {
                placement,
                title: options?.title || '',
                text: options?.text || '',
            },
        });
        await modal.present();
        await modal.onWillDismiss();
        Observable.emit(ObservableEvent.UserChanged);
    }

    async checkout(payload: {
        first_name: string;
        last_name: string;
        discount_code?: string;
        consent_accepted: boolean;
        return_url?: string;
    }): Promise<any> {
        return this.request('POST', 'checkout', {
            ...payload,
            consent_accepted: payload.consent_accepted ? true : false,
        });
    }

    async status(): Promise<any> {
        return this.request('GET', 'status');
    }

    async cancelSubscription(): Promise<any> {
        return this.request('POST', 'cancel');
    }

    async updatePaymentMethod(returnUrl?: string): Promise<any> {
        return this.request('POST', 'payment-method', returnUrl ? {return_url: returnUrl} : {});
    }

    async deletePaymentMethod(): Promise<any> {
        return this.request('DELETE', 'payment-method');
    }

    async applyDiscount(code: string): Promise<any> {
        return this.request('POST', 'discount', {discount_code: code});
    }

    async syncSubscriptionStatus(): Promise<boolean> {
        try {
            const status = await this.status();
            const active = Boolean(
                status?.is_premium
                || status?.status === 'trialing'
                || status?.status === 'active'
                || status?.status === 'past_due',
            );
            if (active) {
                await ApiService.User().get();
                Observable.emit(ObservableEvent.UserChanged);
            }
            return active;
        } catch {
            return false;
        }
    }

    /** @deprecated Verotel path — kept for cutover callers */
    async createPayment(_packageId: string): Promise<string | null> {
        return null;
    }

    async plans(): Promise<any> {
        const status = await this.status().catch(() => null);
        if (status?.list_amount != null) {
            return [{
                packageId: 'pineapple_premium_monthly',
                name: 'Pineapple Premium',
                price: (Number(status.list_amount) / 100).toFixed(2),
                currency: status.currency || 'GBP',
                interval: 'month',
                priceLabel: `${(Number(status.list_amount) / 100).toFixed(2)} ${status.currency || 'GBP'} / month`,
            }];
        }

        return [{
            packageId: 'pineapple_premium_monthly',
            name: 'Pineapple Premium',
            price: '9.99',
            currency: 'GBP',
            interval: 'month',
            priceLabel: '9.99 GBP / month',
        }];
    }
}

export const billingService = new BillingService();
